import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { DefaultButtonStyled } from '@/components/atom/buttons/DefaultButton';
import AlertInformationCircle from '@/components/atom/icon/AlertInformationCircle';
import DotMenu from '@/components/atom/icon/DotMenu';
import EditViewOff from '@/components/atom/icon/EditViewOff';
import FavoriteHeart from '@/components/atom/icon/FavoriteHeart';
import Share from '@/components/atom/icon/Share';
import AutoHeightImage from '@/components/atom/images/AutoHeightImage';
import CommunityUserNickName from '@/components/molecules/CommunityUserNickName';
import ProfileImage from '@/components/molecules/ProfileImage';
import theme from '@/styles/theme';
import { AttachmentBlindType } from '@/types/bff/gql-schema';
import { BoardPostDetail } from '@/types/community';
import { redirectPostImage } from '@/utils/image';

interface Props {
  boardPostDetail: BoardPostDetail;
  updateLikePostStatus: (isLike: boolean) => void;
  onReport: (e: React.MouseEvent) => void;
  onShowShareModal: () => void;
}
const PostContent = ({
  boardPostDetail,
  updateLikePostStatus,
  onReport,
  onShowShareModal,
}: Props) => {
  const [isVisibleImage, setIsVisibleImage] = useState<boolean | null>(null);

  const profileImage = useMemo(() => {
    return boardPostDetail.communityUser.profileImageUrl;
  }, []);

  const onChangeViewSecretImage = () => {
    setIsVisibleImage(true);
  };

  useEffect(() => {
    if (boardPostDetail.isMine) return setIsVisibleImage(true);
    if (boardPostDetail.boardPostAttachments.length) {
      const isBlindImage =
        boardPostDetail.boardPostAttachments[0].blindType ===
        AttachmentBlindType.Blinded;
      return setIsVisibleImage(!isBlindImage);
    }
  }, [boardPostDetail.isMine, boardPostDetail.boardPostAttachments.length]);

  return (
    <Container>
      <Top>
        <Title>{boardPostDetail.title}</Title>
        <Content>{boardPostDetail.content}</Content>
        {isVisibleImage !== null &&
          !!boardPostDetail.boardPostAttachments.length && (
            <ImageContainer>
              <ImageWrapper>
                {isVisibleImage ? (
                  <AutoHeightImage
                    src={redirectPostImage(
                      boardPostDetail.boardPostAttachments[0]
                        .boardPostAttachmentId,
                    )}
                  />
                ) : (
                  <SecretImage>
                    <EditViewOff />
                    <h2>숨겨진 이미지</h2>
                    <h3>주의가 필요한 내용일 수 있어요.</h3>
                    <SecretImageViewButton onClick={onChangeViewSecretImage}>
                      이미지 보기
                    </SecretImageViewButton>
                  </SecretImage>
                )}
              </ImageWrapper>
              {boardPostDetail.isMine &&
                boardPostDetail.boardPostAttachments[0].blindType ===
                  AttachmentBlindType.Blinded && (
                  <SecretImageInfo>
                    <AlertInformationCircle /> 이미지가 숨김 처리되었어요.
                  </SecretImageInfo>
                )}
            </ImageContainer>
          )}
        <UserInfo>
          <ProfileInfo>
            <ProfileImage width={32} height={32} src={profileImage} />
            <Author>
              <CommunityUserNickName
                userId={boardPostDetail.communityUser.userId}
                nickName={boardPostDetail.communityUser.nickname}
              />
              <Date>{boardPostDetail.formattedCreatedAt}</Date>
            </Author>
          </ProfileInfo>
          <AsideIcon>
            <Icon onClick={(e) => onReport(e)}>
              <DotMenu />
            </Icon>
          </AsideIcon>
        </UserInfo>
      </Top>
      <Bottom>
        <BottomButton
          active={boardPostDetail.isLikedByMe}
          onClick={() => updateLikePostStatus(!boardPostDetail.isLikedByMe)}
        >
          <FavoriteHeart
            color={theme.color.gray400}
            isActive={boardPostDetail.isLikedByMe}
          />
          공감해요 {boardPostDetail.likeCount ? boardPostDetail.likeCount : ''}
        </BottomButton>
        <BottomButton onClick={onShowShareModal}>
          <Share color={theme.color.gray400} />
          공유하기
        </BottomButton>
      </Bottom>
    </Container>
  );
};

export default PostContent;

const Container = styled.article`
  margin-bottom: 12px;

  ${({ theme }) => `
    background: ${theme.color.white};
  `}
`;

const Top = styled.div`
  padding: 24px;
`;

const Title = styled.h1`
  margin-bottom: 16px;

  ${({ theme }) => `
    ${theme.typography.UI24SB};
    color: ${theme.color.gray800};
  `}
`;

const Content = styled.h1`
  padding-bottom: 24px;
  white-space: break-spaces;

  ${({ theme }) => `
    ${theme.typography.ARTICLE16R};
    color: ${theme.color.gray600};
  `};
`;

const ImageContainer = styled.div`
  margin-bottom: 24px;
`;

const ImageWrapper = styled.div`
  overflow: hidden;
  border-radius: 8px;
`;

const SecretImageInfo = styled.div`
  margin-top: 16px;

  svg {
    margin-right: 8px;
  }
  ${({ theme }) => `
    ${theme.typography.UI14M};
    color: ${theme.color.blue500};
  `};
`;
const SecretImage = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 0;
  align-items: center;

  ${({ theme }) => `
    background: ${theme.color.red100};

    svg {
      margin-bottom: 8px;
    }
    h2 {
      ${theme.typography.UI16SB};
      color: ${theme.color.gray700};
      margin-bottom: 4px;
    }
    h3 {
      ${theme.typography.UI14M};
      color: ${theme.color.gray500};
      margin-bottom: 16px;
    }
  `};
`;

const SecretImageViewButton = styled(DefaultButtonStyled)`
  padding: 10px 16px;
  border-radius: 8px;

  ${({ theme }) => `
    ${theme.typography.UI14M};
    color: ${theme.color.gray600};
    background: ${theme.color.white};
    border: 1px solid ${theme.color.gray100};

    &: active {
      background: ${theme.color.gray50};
    }
  `};
`;

const UserInfo = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ProfileInfo = styled.div`
  display: flex;
  align-items: center;
`;

const Author = styled.div`
  flex-direction: column;
  display: flex;
  margin-left: 12px;
`;

const Date = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI12R};
    color: ${theme.color.gray300};
  `};
`;

const AsideIcon = styled.div`
  display: flex;
  align-items: center;
`;

const Icon = styled(DefaultButtonStyled)`
  padding: 6px;
  border-radius: 6px;
  ${({ theme }) => `
    &: active {
      background: ${theme.color.gray50}
    }
  `};
`;

const Bottom = styled.div`
  display: flex;
  justify-content: center;
`;

const BottomButton = styled(DefaultButtonStyled)<{ active?: boolean }>`
  flex: 1;
  align-items: center;
  padding: 18px 0;

  ${({ theme, active }) => `
    ${theme.typography.UI14M};
    color: ${active ? theme.color.primary500 : theme.color.gray400};
    border: 1px solid ${theme.color.gray50};

    &: active {
      background: ${theme.color.gray50};
    }
  `};

  &:not(first-child) {
    border-left: none;
  }

  svg {
    margin-right: 8px;
  }
`;
